/**
 * Ver. 2026/09/01
 * お気に入り（Wishlist）フロント操作
 * - ボタン押下で楽観的にUI更新 → App Proxy(/apps/wishlist/toggle) へ送信
 * - 送信ボディは { variant_id }（バリエーション単位）
 * - サーバ応答 { in_wishlist, count } でUIを確定
 * - バリエーション切替時は data-variant-id と♡/♥を同期
 * - 401（未ログイン/セッション切れ）→ ログインモーダル表示
 * - 409（上限超過）等のメッセージ通知、その他失敗はロールバック
 *
 * バックエンド：ecsergeant.com（App Proxy /apps/wishlist）稼働済み。
 * 設定は window.AT_WISHLIST（snippets/at-wishlist-header.liquid で出力）。
 */
import { ThemeEvents } from '@theme/events';

const config = window.AT_WISHLIST || {
  endpoint: '/apps/wishlist',
  loggedIn: false,
  loginUrl: '/account/login',
  count: 0,
  variantIds: [],
};

const PENDING_KEY = 'at_wishlist_pending';

let currentCount = Number(config.count) || 0;
const wishlistVariantIds = new Set(
  (Array.isArray(config.variantIds) ? config.variantIds : []).map(Number).filter(Boolean)
);

/** ログイン後に現在のページへ戻るURL（新・顧客アカウントの return_to） */
function getLoginUrl() {
  const returnPath = window.location.pathname + window.location.search;
  const base = config.loginUrl || '/customer_authentication/login';
  const sep = base.includes('?') ? '&' : '?';
  return `${base}${sep}return_to=${encodeURIComponent(returnPath)}`;
}

/** 未ログイン時：モーダル表示（なければ既存アカウントUI、最後は遷移） */
function openLoginPrompt(variantId) {
  if (variantId) {
    sessionStorage.setItem(PENDING_KEY, String(variantId));
  }

  const dialogEl = document.getElementById('at-wishlist-login-dialog');
  if (dialogEl && typeof dialogEl.showDialog === 'function') {
    dialogEl.showDialog();
    return;
  }

  const drawer = document.querySelector('.account-drawer dialog-component');
  if (drawer && typeof drawer.showDialog === 'function') {
    drawer.showDialog();
    return;
  }

  const popover = document.querySelector('.account-popover');
  if (popover) {
    popover.setAttribute('open', '');
    return;
  }

  window.location.href = getLoginUrl();
}

/** ログイン完了後、保留中のバリエーションをお気に入りに追加 */
function processPendingWishlist() {
  if (!config.loggedIn) return;

  const variantId = sessionStorage.getItem(PENDING_KEY);
  if (!variantId) return;

  sessionStorage.removeItem(PENDING_KEY);

  const button = document.querySelector(
    `[data-at-wishlist-toggle][data-variant-id="${CSS.escape(variantId)}"]`
  );
  if (button && !button.classList.contains('is-active') && !button.classList.contains('is-loading')) {
    toggle(button);
  }
}

function updateBadge(count) {
  currentCount = Math.max(0, count);
  document.querySelectorAll('[data-at-wishlist-count]').forEach((el) => {
    el.textContent = String(currentCount);
    el.classList.toggle('is-hidden', currentCount === 0);
  });
  document.querySelectorAll('[data-at-wishlist-header]').forEach((el) => {
    el.setAttribute('aria-label', `お気に入り（${currentCount}件）`);
  });
}

/**
 * @param {HTMLElement} button
 * @param {boolean} active
 * @param {{ animate?: boolean }} [options]
 */
function setButtonState(button, active, options = {}) {
  const animate = options.animate !== false;
  button.classList.toggle('is-active', active);
  if (animate) {
    button.classList.add('at-wishlist-button--pop');
  }
  button.setAttribute('aria-pressed', active ? 'true' : 'false');
  const label = active ? 'お気に入りから削除' : 'お気に入りに追加';
  button.setAttribute('aria-label', label);
  button.setAttribute('title', label);
}

/** バリエーション切替に合わせてボタンの対象IDと♡/♥を更新 */
function applyVariantToButton(button, variantId) {
  button.dataset.variantId = String(variantId);
  setButtonState(button, wishlistVariantIds.has(Number(variantId)), { animate: false });
}

/** 一覧ページで解除したカードを取り除き、空なら空状態を出す */
function removeWishlistCardIfNeeded(button) {
  const item = button.closest('.at-wishlist-page__item');
  if (!item) return;

  const grid = item.closest('.at-wishlist-page__grid');
  item.remove();

  if (grid && !grid.querySelector('.at-wishlist-page__item')) {
    const page = grid.closest('.at-wishlist-page__inner');
    grid.remove();
    if (!page || page.querySelector('.at-wishlist-page__empty')) return;

    const empty = document.createElement('div');
    empty.className = 'at-wishlist-page__empty';
    const pageRoot = page.closest('.at-wishlist-page');
    const emptyText =
      (pageRoot && pageRoot.getAttribute('data-at-wishlist-empty-text')) ||
      'お気に入りに登録された商品はまだありません。';
    const allUrl = (pageRoot && pageRoot.getAttribute('data-at-wishlist-all-url')) || '/collections/all';
    const emptyP = document.createElement('p');
    emptyP.textContent = emptyText;
    const emptyLink = document.createElement('a');
    emptyLink.href = allUrl;
    emptyLink.className = 'button-secondary';
    emptyLink.textContent = '商品を探す';
    empty.append(emptyP, emptyLink);
    page.appendChild(empty);
  }
}

async function toggle(button) {
  const variantId = Number(button.dataset.variantId);
  if (!variantId) return;

  // テーマエディタ（design mode）/ CLIプレビューでは App Proxy が動かず、
  // 顧客ログインもできないため、見た目のトグルだけして終了（遷移・通信しない）。
  if (config.designMode) {
    const active = !button.classList.contains('is-active');
    setButtonState(button, active);
    updateBadge(currentCount + (active ? 1 : -1));
    console.info('[at-wishlist] design mode: 表示のみ切替（保存は実ストアで動作）');
    return;
  }

  if (!config.loggedIn) {
    openLoginPrompt(variantId);
    return;
  }

  const wasActive = button.classList.contains('is-active');
  const nextActive = !wasActive;

  // 楽観的更新
  setButtonState(button, nextActive);
  updateBadge(currentCount + (nextActive ? 1 : -1));
  button.classList.add('is-loading');
  button.disabled = true;

  try {
    const res = await fetch(`${config.endpoint}/toggle`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ variant_id: variantId }),
    });

    // 未ログイン/セッション切れ → モーダル表示
    if (res.status === 401) {
      setButtonState(button, wasActive);
      updateBadge(currentCount + (nextActive ? -1 : 1));
      openLoginPrompt(variantId);
      return;
    }

    let data = null;
    try {
      data = await res.json();
    } catch (_) {
      /* JSON以外の応答 */
    }

    if (!res.ok) {
      // 上限超過(409)など、サーバのメッセージがあれば通知
      const userMessage = data && data.message ? data.message : null;
      const err = new Error(`status ${res.status}`);
      err.userMessage = userMessage;
      throw err;
    }

    // サーバの結果を正とする
    const inWishlist = data && typeof data.in_wishlist === 'boolean' ? data.in_wishlist : nextActive;
    setButtonState(button, inWishlist);
    if (inWishlist) {
      wishlistVariantIds.add(variantId);
    } else {
      wishlistVariantIds.delete(variantId);
      removeWishlistCardIfNeeded(button);
    }

    if (data && typeof data.count === 'number') {
      updateBadge(data.count);
    }

    // 同じバリエーションの他ボタン（カードとPDPなど）も揃える
    document.querySelectorAll(`[data-at-wishlist-toggle][data-variant-id="${variantId}"]`).forEach((other) => {
      if (other === button) return;
      setButtonState(other, inWishlist, { animate: false });
    });
  } catch (err) {
    // 失敗：ロールバック
    console.warn('[at-wishlist] toggle failed, rolling back:', err);
    setButtonState(button, wasActive);
    updateBadge(currentCount + (nextActive ? -1 : 1));
    if (err && err.userMessage) {
      window.alert(err.userMessage);
    }
  } finally {
    button.classList.remove('is-loading');
    button.disabled = false;
  }
}

/**
 * バリエーションセレクタ変更時：対象ボタンの data-variant-id と登録状態を更新。
 * 商品カードは variant:update の伝播を止めるためキャプチャで受け取る。
 * @param {CustomEvent} event
 */
function onVariantUpdate(event) {
  const variant = event.detail && event.detail.resource;
  if (!variant || !variant.id) return;

  const variantId = String(variant.id);
  const productId = event.detail.data && event.detail.data.productId ? String(event.detail.data.productId) : '';
  const card = event.target instanceof Element ? event.target.closest('product-card') : null;

  document.querySelectorAll('[data-at-wishlist-toggle]').forEach((button) => {
    if (card) {
      if (!card.contains(button)) return;
    } else {
      if (button.closest('product-card')) return;
      if (productId && button.dataset.productId && button.dataset.productId !== productId) return;
    }
    applyVariantToButton(button, variantId);
  });
}

document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-at-wishlist-toggle]');
  if (!button) return;
  event.preventDefault();
  event.stopPropagation();
  if (button.classList.contains('is-loading')) return;
  toggle(button);
});

document.addEventListener(
  'animationend',
  (event) => {
    if (event.target.closest('[data-at-wishlist-toggle]')) {
      event.target.closest('[data-at-wishlist-toggle]').classList.remove('at-wishlist-button--pop');
    }
  },
  true
);

document.addEventListener(ThemeEvents.variantUpdate, onVariantUpdate, true);

// 初期バッジ同期
updateBadge(currentCount);

// ログイン後の保留操作を実行
processPendingWishlist();
